import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { StudyPlan, OnboardingData } from '../../types/onboarding';
import { CheckCircle2, ChevronRight, Calendar } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { useEntitlements } from '../../hooks/useEntitlements';
import { getChapterAccess } from '../../catalog/chapters';
import { getFlashcardSetAccess } from '../../catalog/flashcards';
import { getStudyState } from '../../services/studyPlanStore';
import { getDaysUntilExam } from '../../services/onboardingPlan';
import {
    getTodayMissionProgress,
    isTaskCompleted,
    areAllTasksCompleted,
    TaskCompletionStatus
} from '../../services/missionTaskCompletion';
import { useStudyPlanManager } from '../../hooks/useStudyPlanManager';
import { CircularProgress } from '../common/CircularProgress';

interface Props {
    userName: string;
    onSwitchToOSP: () => void;
    showWelcome?: boolean;
}

const StudyPlanWidget: React.FC<Props> = ({ userName, onSwitchToOSP, showWelcome = false }) => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { hasPlus } = useEntitlements();
    const [plan, setPlan] = useState<StudyPlan | null>(null);
    const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(null);
    // showWelcome is now a prop

    const loadPlan = async () => {
        if (currentUser) {
            try {
                console.log("Loading study plan for user:", currentUser.uid);
                const state = await getStudyState(currentUser.uid);
                if (state && state.studyPlan) {
                    console.log("Study plan loaded:", state.studyPlan.todayMission.label);
                    setPlan(state.studyPlan);
                    setOnboardingData(state.onboardingData);
                } else {
                    console.log("No study plan found");
                    setPlan(null);
                }
            } catch (e) {
                console.error("Failed to load study plan", e);
                setPlan(null);
            }
        }
    };

    useEffect(() => {
        loadPlan();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    // Reload when component mounts (for when returning from wizard)
    useEffect(() => {
        loadPlan();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reload when window regains focus (in case plan was saved in another tab)
    useEffect(() => {
        const handleFocus = () => {
            loadPlan();
            // Also reload task progress from localStorage
            if (plan) {
                const progress = getTodayMissionProgress(plan);
                setTaskProgress(progress);
                console.log('[StudyPlanWidget] Task progress reloaded on focus:', progress);
            }
        };
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, plan]);

    // Listen for task completion events from modules
    useEffect(() => {
        const handleTaskCompleted = () => {
            if (plan) {
                const progress = getTodayMissionProgress(plan);
                setTaskProgress(progress);
                console.log('[StudyPlanWidget] Task progress reloaded after completion:', progress);
            }
        };
        window.addEventListener('taskCompleted', handleTaskCompleted);
        return () => window.removeEventListener('taskCompleted', handleTaskCompleted);
    }, [plan]);

    // Listen for study plan updates (e.g., from admin time travel)
    useEffect(() => {
        const handlePlanUpdated = () => {
            console.log('[StudyPlanWidget] Plan updated, reloading...');
            loadPlan();
        };
        window.addEventListener('studyPlanUpdated', handlePlanUpdated);
        return () => window.removeEventListener('studyPlanUpdated', handlePlanUpdated);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [taskProgress, setTaskProgress] = useState<TaskCompletionStatus[]>([]);
    const isCompleted = areAllTasksCompleted(plan);

    // Secret Gesture State
    const [clickCount, setClickCount] = useState(0);
    const { simulateNextDay } = useStudyPlanManager();

    const handleSecretGesture = async () => {
        if (currentUser?.email !== 'camicaillie@gmail.com') return;

        const newCount = clickCount + 1;
        setClickCount(newCount);

        if (newCount >= 5) {
            setClickCount(0);
            if (window.confirm("Admin Time Travel: Skip to next day?")) {
                await simulateNextDay();
                // Reload plan immediately
                setTimeout(loadPlan, 500);
            }
        } else {
            // Reset count if not clicked fast enough
            setTimeout(() => setClickCount(0), 2000);
        }
    };

    useEffect(() => {
        if (plan) {
            const progress = getTodayMissionProgress(plan);
            setTaskProgress(progress);
        }
    }, [plan]);

    // Check if a task is locked
    const isTaskLocked = useCallback((task: any) => {
        if (hasPlus) return false;
        if (!task.deepLink) return false;

        try {
            const url = new URL(task.deepLink, 'http://localhost');
            const params = url.searchParams;

            // Check based on task type or deep link structure
            // Chapters
            const chapterId = params.get('chapter');
            if (chapterId) {
                return getChapterAccess(chapterId) === 'pro';
            }

            // Flashcards
            // Assuming flashcards link is like /flashcards?topic=topicId or similar
            // In Dashboard, flashcards use preSelectedTopicId which works via handleNavigation
            // Deep links are usually constructed in generatePlan. Let's check typical structure.
            // A typical flashcard link: /flashcards?topic=psychologie
            const topicId = params.get('topic');
            if (topicId && (url.pathname.includes('flashcards') || task.type === 'flashcards')) {
                return getFlashcardSetAccess(topicId) === 'pro';
            }

            return false;
        } catch (e) {
            console.error("Error parsing deepLink for lock check:", e);
            return false;
        }
    }, [hasPlus]);

    const handleStartMission = () => {
        if (plan && plan.todayMission.tasks.length > 0) {
            // Stable Selection: Find first uncompleted task
            const today = new Date().toISOString().split('T')[0];

            // Find first uncompleted task using the new task-based system
            const task = plan.todayMission.tasks.find(t => !isTaskCompleted(t.id)) || plan.todayMission.tasks[0]; // Fallback to first if all done

            // Check if locked
            if (isTaskLocked(task)) {
                console.log('[StudyPlanWidget] Task is locked, redirecting to upgrade');
                navigate('/upgrade');
                return;
            }

            let params = new URLSearchParams();
            let path = '/osp'; // Default to OSP Tutor

            if (task.deepLink) {
                try {
                    // Check if it's ZSV deep link or OSP
                    if (task.deepLink.startsWith('/')) {
                        // It's a relative path like /textbook?chapter=...
                        const url = new URL(task.deepLink, 'http://localhost');
                        path = url.pathname;
                        params = url.searchParams;
                    } else {
                        // absolute or legacy
                        const url = new URL(task.deepLink, 'http://localhost');
                        path = url.pathname;
                        params = url.searchParams;
                    }

                    // Handle legacy /?mode=osp links
                    if (path === '/' && params.get('mode') === 'osp') {
                        path = '/osp';
                    }
                } catch (e) {
                    console.error("Invalid deepLink:", task.deepLink);
                }
            }

            const type = params.get('type') || (task as any).kind || (task as any).type || 'training';
            const focus = params.get('focus') || (task as any).focus || 'general';
            const minutes = params.get('minutes') || task.minutes || 15;

            // Map types to human labels (fix for "Obecné")
            let label = task.label;
            if (!label) {
                if (type === 'diagnostic') label = "Diagnostický test";
                else if (type === 'training') label = `Trénink: ${focus === 'bzs' ? 'Základy spol. věd' : focus}`;
                else if (type === 'recap') label = "Rychlé opakování";
                else if (type === 'chapter') label = "Kapitola";
                else if (type === 'flashcards') label = "Flashkarty";
                else if (type === 'mock') label = "Mock Test";
                else label = "Dnešní mise";
            }

            const missionData = {
                mode: 'mission',
                type,
                focus,
                minutes,
                label,
                module: path === '/osp' || path === '/tutor' ? 'osp' : 'zsv', // Infer module
                missionKey: `${today}_${task.id}`,
                taskId: task.id,
                createdAt: Date.now()
            };

            // Set pending
            localStorage.setItem('pendingMission', JSON.stringify(missionData));

            // Explicit start -> Undismiss if previously dismissed!
            const dismissed = JSON.parse(localStorage.getItem('dismissedMissionKeys') || '{}');
            if (dismissed[missionData.missionKey]) {
                delete dismissed[missionData.missionKey];
                localStorage.setItem('dismissedMissionKeys', JSON.stringify(dismissed));
            }

            // Navigation
            if (path === '/tutor' || path === '/osp') {
                navigate('/osp' + (params.toString() ? '?' + params.toString() : ''));
            } else {
                navigate(path + '?' + params.toString());
            }
        }
    };

    const handleTaskClick = (task: any) => {
        console.log('[StudyPlanWidget] handleTaskClick called:', task.id, task.deepLink);

        if (isTaskLocked(task)) {
            console.log('[StudyPlanWidget] Task is locked, redirecting to upgrade');
            navigate('/upgrade');
            return;
        }

        // Optional: If locked and we want to prevent navigation/show upgrade here directly, we could.
        // For now, we allow navigation so the user hits the standardized paywall in the content modules.

        const today = new Date().toISOString().split('T')[0];
        // LocalStorage Handoff for specific task
        let params = new URLSearchParams();
        let path = '/osp';

        if (task.deepLink) {
            try {
                const url = new URL(task.deepLink, 'http://localhost');
                path = url.pathname;
                params = url.searchParams;

                // Handle legacy /?mode=osp links
                if (path === '/' && params.get('mode') === 'osp') {
                    path = '/osp';
                }

                console.log('[StudyPlanWidget] Parsed deepLink:', { path, params: params.toString() });
            } catch (e) {
                console.error("Invalid deepLink", task.deepLink);
            }
        }

        const type = params.get('type') || (task as any).type || 'training';
        const focus = params.get('focus') || (task as any).focus || 'general';
        const minutes = params.get('minutes') || task.minutes || 15;

        // Map types to human labels (fix for "Obecné")
        let label = task.label;
        if (!label) {
            if (type === 'diagnostic') label = "Diagnostický test";
            else if (type === 'training') label = `Trénink: ${focus === 'bzs' ? 'Základy spol. věd' : focus}`;
            else if (type === 'recap') label = "Rychlé opakování";
            else if (type === 'chapter') label = "Kapitola";
            else if (type === 'flashcards') label = "Flashkarty";
            else if (type === 'mock') label = "Mock Test";
            else label = "Dnešní mise";
        }

        const missionData = {
            mode: 'mission',
            type,
            focus,
            minutes,
            label,
            module: path === '/osp' || path === '/tutor' ? 'osp' : 'zsv',
            missionKey: `${today}_${task.id}`,
            taskId: task.id,
            createdAt: Date.now()
        };
        localStorage.setItem('pendingMission', JSON.stringify(missionData));

        // Explicit start -> Undismiss
        const dismissed = JSON.parse(localStorage.getItem('dismissedMissionKeys') || '{}');
        if (dismissed[missionData.missionKey]) {
            delete dismissed[missionData.missionKey];
            localStorage.setItem('dismissedMissionKeys', JSON.stringify(dismissed));
        }

        if (path === '/tutor' || path === '/osp') {
            console.log('[StudyPlanWidget] Switching to OSP');
            navigate('/osp' + (params.toString() ? '?' + params.toString() : ''));
        } else {
            const navigateUrl = path + '?' + params.toString();
            console.log('[StudyPlanWidget] Navigating to:', navigateUrl);
            navigate(navigateUrl);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const getTimeBasedGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Dobré ráno';
        if (hour < 17) return 'Dobré odpoledne';
        return 'Dobrý večer';
    };

    if (!plan) {
        return (
            <div className="relative overflow-hidden bg-white dark:bg-dark-card rounded-3xl h-full p-8 md:p-10 text-foreground border border-border shadow-[0_4px_12px_rgba(0,0,0,0.1)] dark:shadow-none">
                <div className="flex flex-col items-center justify-center h-full text-center space-y-6">
                    {/* Icon/Emoji */}
                    <div className="text-6xl" role="img" aria-label="Studijní plán">
                        📚
                    </div>

                    {/* Heading */}
                    <h2 className="text-2xl md:text-3xl font-bold font-poppins text-light-text-primary dark:text-dark-text-primary">
                        Vytvoř si personalizovaný plán
                    </h2>

                    {/* Description */}
                    <p className="text-base md:text-lg text-light-text-secondary dark:text-dark-text-secondary max-w-md leading-relaxed">
                        Nastav předměty, termíny zkoušek a získej strukturované denní úkoly.
                    </p>

                    {/* Primary CTA Button */}
                    <button
                        onClick={() => navigate('/onboarding')}
                        className="w-full sm:w-auto px-8 py-3.5 bg-brand-orange text-white text-lg rounded-xl font-bold font-poppins hover:bg-brand-orange/90 transition-all shadow-lg border-b-[6px] border-orange-500 active:border-b-2 active:translate-y-[4px] flex items-center justify-center gap-2 group mt-2"
                    >
                        Vytvořit studijní plán
                        <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>

                    {/* Secondary Link */}
                    <button
                        onClick={() => {
                            // Just a no-op - user can explore the app freely
                            // The empty state will remain until they create a plan
                        }}
                        className="text-sm text-muted-foreground hover:text-light-text-secondary dark:hover:text-dark-text-secondary transition-colors underline-offset-4 hover:underline"
                    >
                        Prozkoumám aplikaci nejdřív
                    </button>
                </div>
            </div>
        );
    }

    const trackLabel = plan.todayMission.tasks[0]?.module || (plan.focusSplit.osp && plan.focusSplit.zsv ? 'Obojí' : plan.focusSplit.osp ? 'OSP' : 'ZSV');
    const daysUntilExam = onboardingData?.examDate ? getDaysUntilExam(onboardingData.examDate) : null;

    return (
        <div className="relative overflow-hidden bg-white dark:bg-dark-card rounded-3xl h-full p-6 text-light-text-primary dark:text-dark-text-primary border border-border shadow-[0_4px_12px_rgba(0,0,0,0.1)] dark:shadow-none hover:shadow-[0_6px_16px_rgba(0,0,0,0.12)] dark:hover:shadow-md transition-shadow duration-300">
            <div className="flex flex-col h-full">
                {/* Today's Mission Header with countdown */}
                <div className="mb-6 relative">
                    <div className="flex items-center justify-between mb-1 relative z-10">
                        <h1
                            className="text-2xl font-bold font-poppins flex items-center gap-2 select-none"
                            onClick={handleSecretGesture}
                        >
                            {isCompleted ? (
                                <CheckCircle2 className="w-6 h-6 text-green-500" />
                            ) : (
                                <CheckCircle2 className="w-6 h-6 text-brand-orange" />
                            )}
                            {isCompleted ? "Mise splněna!" : (
                                showWelcome ? (
                                    <span className="bg-gradient-to-r from-brand-orange to-orange-500 bg-clip-text text-transparent transform scale-105 origin-left transition-transform">
                                        Vítej! Tady je tvoje první mise
                                    </span>
                                ) : "Dnešní mise"
                            )}
                        </h1>
                        {daysUntilExam !== null && daysUntilExam > 0 && (
                            <div className={`flex items-center gap-1 text-xs font-medium ${daysUntilExam <= 14 ? 'text-red-500' :
                                daysUntilExam <= 30 ? 'text-yellow-500' :
                                    'text-green-500'
                                }`}>
                                <Calendar className="w-3 h-3" />
                                {daysUntilExam} dní do zkoušky
                            </div>
                        )}
                    </div>
                    <p className="text-light-text-secondary dark:text-dark-text-secondary ml-8">
                        {isCompleted ? "Skvělá práce! Pokračuj zítra." : `Celkem ${plan.todayMission.estimatedMinutes} min • ${trackLabel}`}
                    </p>
                </div>

                {/* Two-column grid layout: Progress circle (left) and Tasks (right) */}
                <div className={`grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-8 sm:gap-12 items-center ${isCompleted ? 'opacity-50 grayscale transition-all duration-500' : ''}`}>
                    {/* Left column: Progress circle */}
                    <div className="flex justify-center items-center">
                        <CircularProgress
                            percentage={taskProgress.length > 0 ? (taskProgress.filter(t => t.completed).length / taskProgress.length) * 100 : 0}
                            completedTasks={taskProgress.filter(t => t.completed).length}
                            totalTasks={taskProgress.length}
                            size={160}
                            strokeWidth={14}
                            animate={true}
                        />
                    </div>

                    {/* Right column: Tasks List */}
                    <div className="space-y-3">
                        {plan.todayMission.tasks.slice(0, 3).map((task, index) => {
                            const taskIsCompleted = isTaskCompleted(task.id);
                            const locked = isTaskLocked(task);

                            return (
                                <div
                                    key={task.id}
                                    onClick={() => handleTaskClick(task)}
                                    className="flex items-center justify-between py-2 px-3 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg transition-colors cursor-pointer group"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className={`flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold transition-colors ${taskIsCompleted
                                            ? 'bg-green-100 text-green-600 dark:bg-green-900/30'
                                            : 'bg-secondary dark:bg-white/10 text-light-text-secondary dark:text-dark-text-primary group-hover:bg-brand-orange/10 group-hover:text-brand-orange'
                                            }`}>
                                            {taskIsCompleted ? <CheckCircle2 className="w-4 h-4" /> : index + 1}
                                        </span>
                                        <span className={`text-base font-semibold transition-colors flex items-center ${!taskIsCompleted && 'group-hover:text-brand-orange'}`}>
                                            {task.label}
                                            {locked && !taskIsCompleted && (
                                                <div className="ml-2 bg-yellow-400 text-yellow-950 text-[10px] font-black tracking-wide px-1.5 py-0.5 rounded-md shadow-sm border-b-2 border-yellow-600 flex items-center">
                                                    PRO
                                                </div>
                                            )}
                                        </span>
                                    </div>
                                    <span className={`text-sm font-semibold text-light-text-secondary dark:text-dark-text-secondary ${locked && !taskIsCompleted ? 'hidden sm:inline' : ''}`}>{task.minutes} min</span>
                                </div>
                            );
                        })}
                        {plan.todayMission.tasks.length > 3 && (
                            <p className="pt-2 text-xs text-center text-muted-foreground">+ {plan.todayMission.tasks.length - 3} další úkoly</p>
                        )}
                    </div>
                </div>

                {/* Footer CTA */}
                <div className="mt-4 pt-0">
                    <button
                        onClick={handleStartMission}
                        className={`w-full py-3.5 text-white text-lg rounded-xl font-bold font-poppins transition-all border-b-[6px] active:border-b-2 active:translate-y-[4px] shadow-lg flex items-center justify-center gap-2 group ${isCompleted
                            ? 'bg-green-600 border-green-700 hover:bg-green-600/90 shadow-green-600/20'
                            : 'bg-brand-orange border-orange-500 hover:bg-brand-orange/90 shadow-orange-500/20'
                            }`}
                    >
                        {isCompleted ? "Zopakovat misi" : "Spustit dnešní misi"}
                        <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StudyPlanWidget;
